import { useEffect, useRef } from "react";

import { ApiError, USER_KEY, api } from "../api/client";
import Contributor from "../types/Contributor";
import { ProviderResult } from "../types/ProviderResult";
import Segment from "../types/Segment";
import Series from "../types/Series";
import Work, { WorkType } from "../types/Work";
import { useApi } from "./useApi";
import { usePagedApi } from "./usePagedApi";

// The catalog's reads and adds.

export const getWork = (id: number) => api.get<Work>(`/works/${id}`);

// Add a title the catalog lacks, from a source's result or typed in.
export const addWork = (work: object) => api.post<Work>("/works", work);

// What a source (TMDB, Open Library, IGDB) knows by that name.
export const providerSearch = (query: string, workType: WorkType) =>
  api.get<ProviderResult[]>(
    `/works/provider-search?${new URLSearchParams({ q: query, type: workType })}`,
  );

// The shown catalog book holding `isbn`, or null when none does.
export const workWithIsbn = async (isbn: string): Promise<Work | null> => {
  try {
    return await api.get<Work>(`/works/isbn/${isbn}`, false);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
};

// The catalog's reads, for a page to show (ADR 0055). A work's page reads
// signed out (`auth` false), the same for everyone; a search reads signed
// in, for the types you track (ADR 0044). Null reads nothing.

export const useWorkPage = <T = Work>(id: number | string | null) =>
  useApi<T>(id === null ? null : `/works/${id}`, { auth: false });
// A work as you see it, signed in.
export const useWork = (id: number | null) =>
  useApi<Work>(id === null ? null : `/works/${id}`);

export const useSeries = (id: number | string) =>
  useApi<Series>(`/series/${id}`, { auth: false });
export const useSeriesWorks = (id: number | string | null) =>
  useApi<Work[]>(id === null ? null : `/series/${id}/works`, { auth: false });

// An episode, with the work it's on.
export const useSegment = <T = Segment>(
  workId: number | string,
  segmentId: number | string,
) => useApi<T>(`/works/${workId}/segments/${segmentId}`, { auth: false });
// A work's episodes, or one season's.
export const useSegments = (
  workId: number | string | null,
  season: number | null,
) =>
  useApi<Segment[]>(
    workId === null
      ? null
      : `/works/${workId}/segments${season !== null ? `?season=${season}` : ""}`,
    { auth: false },
  );

export const useContributor = (id: number | string) =>
  useApi<Contributor>(`/contributors/${id}`);

// The catalog's works by name (`q` null reads nothing), after `debounce`.
export const useWorkSearch = (
  q: string | null,
  limit: number,
  debounce: number,
) =>
  useApi<Work[]>(
    q === null ? null : `/works?q=${encodeURIComponent(q)}&limit=${limit}`,
    { debounce },
  );
// Where the app searches (`SEARCH_SURFACES` in `khipur/core/constants.py`).
export type SearchSurface =
  | "browse"
  | "palette"
  | "quicklog"
  | "list_adder"
  | "add_title";

// Only a signed-in search can be counted; losing one is fine.
const reportSearch = (body: object): void => {
  if (localStorage.getItem(USER_KEY) !== null)
    void api.post("/events", body).catch(() => undefined);
};

/**
 * Report how a search on `surface` ended (plan 014's 6d): `picked(rank)`
 * when one of its results is chosen, from 1; left typed and unpicked when
 * the surface closes, abandoned, saying whether it found anything. A search
 * passed on to another surface (`handedOff`) is that one's. Its words are
 * sent only when they found nothing, for the API to keep apart with no
 * user, 30 days. `found` is how many results show (undefined while loading).
 */
export const useSearchEnd = (
  surface: SearchSurface,
  query: string,
  found: number | undefined,
  workType: WorkType | null = null,
) => {
  // `foundFor`: the query `found` counts the results of.
  const search = useRef({
    query: "",
    found: 0,
    foundFor: "",
    ended: false,
    workType,
  });
  const q = query.trim();
  // Leaving a search unpicked: its box emptied, or the surface closed.
  const left = useRef(() => {
    const { query, found, foundFor, ended, workType } = search.current;
    if (query && !ended)
      reportSearch({
        name: "search_abandoned",
        surface,
        hadResults: found > 0,
        workType: workType ?? undefined,
        // Never words another query's results were counted for.
        query: found === 0 && foundFor === query ? query : undefined,
      });
  });
  useEffect(() => {
    if (!q) left.current();
    // A new query is a new search, until one of its results is picked.
    if (search.current.query !== q) search.current.ended = false;
    search.current.query = q;
    if (found !== undefined) {
      search.current.found = found;
      search.current.foundFor = q;
    }
    search.current.workType = workType;
  }, [q, found, workType]);
  useEffect(() => () => left.current(), []);
  return {
    picked: (rank: number) => {
      if (search.current.ended || !search.current.query) return;
      search.current.ended = true;
      reportSearch({
        name: "search_selected",
        surface,
        hadResults: true,
        rank,
        workType: search.current.workType ?? undefined,
      });
    },
    handedOff: () => {
      search.current.ended = true;
    },
  };
};

// Titles by name, a show once (`q` empty reads nothing), as you type.
export const useTitleSearch = (q: string, limit: number) =>
  useApi<Work[]>(
    q
      ? `/works?one_per_show=true&${new URLSearchParams({ q, limit: String(limit) })}`
      : null,
    { debounce: 200 },
  );
// What a source knows by that name, of a type (null reads nothing).
export const useProviderSearch = (q: string, type: WorkType | null) =>
  useApi<ProviderResult[]>(
    type === null
      ? null
      : `/works/provider-search?${new URLSearchParams({ q, type })}`,
  );
// Browse's titles, a page at a time, by type and name (off reads nothing).
export const useBrowseWorks = (
  on: boolean,
  filters: { type: WorkType | null; q: string },
  pageSize: number,
) => {
  // An edition shown with its original isn't one of its own (plan 013).
  const params = new URLSearchParams({ one_per_show: "true" });
  if (filters.type) params.set("type", filters.type);
  if (filters.q) params.set("q", filters.q);
  return usePagedApi<Work>(on ? `/works?${params}` : null, {
    pageSize,
    keyOf: (work) => work.id,
    debounce: 300,
  });
};
